import React, { useState, useEffect, useContext } from 'react';
import { Row, Col, Card, Container, Button, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Loader from '../components/Loader';
import AuthContext from '../context/AuthContext';

/**
 * HomePage component - displays a list of karaoke videos
 */
const HomePage = () => {
  const { user } = useContext(AuthContext);
  const [videos, setVideos] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get('/api/videos');
        setVideos(data);
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to load videos');
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);
  
  // Handle showing delete confirmation modal
  const handleShowDeleteModal = (video) => {
    setVideoToDelete(video);
    setShowDeleteModal(true);
  };
  
  // Handle delete video
  const handleDeleteVideo = async () => {
    if (!videoToDelete || !user) return;
    
    setDeleteLoading(true);
    
    try {
      await axios.delete(`/api/videos/${videoToDelete.id}`, {
        headers: {
          Authorization: `Bearer ${user.token}`
        }
      });
      
      // Remove deleted video from state
      setVideos(videos.filter(v => v.id !== videoToDelete.id));
      setShowDeleteModal(false);
      setVideoToDelete(null);
    } catch (error) {
      console.error('Error deleting video:', error);
      setError('Failed to delete video');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Filter videos by title
  const filteredVideos = videos.filter(v =>
    (v.title || '').toLowerCase().includes(query.toLowerCase())
  );

  return (
    <Container className="py-4">
      <h1 className="mb-4">Karaoke Videos</h1>
      <div className="mb-3">
        <input
          type="search"
          className="form-control"
          placeholder="Search songs..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      
      {loading ? (
        <div className="d-flex justify-content-center p-5">
          <Loader />
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : filteredVideos.length === 0 ? (
        <div className="alert alert-info">
          No videos found. <Link to="/upload">Upload some videos</Link> to get started.
        </div>
      ) : (
        <ul className="list-group">
          {filteredVideos.map((video, index) => (
            <li key={video.id || index} className="list-group-item d-flex justify-content-between align-items-center">
              <Link to={`/video/${video.id}`} className="text-decoration-none text-truncate" style={{ maxWidth: '70%' }}>
                {video.title}
              </Link>
              <div className="d-flex gap-2">
                <Link to={`/video/${video.id}`} className="btn btn-sm btn-primary">
                  Watch
                </Link>
                <Button 
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleShowDeleteModal(video)}
                >
                  Delete
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete "{videoToDelete?.title}"? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteVideo} disabled={deleteLoading}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

// Export the component as default
export default HomePage;
